import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation } from "wouter";
import { registerSchema, type RegisterInput } from "@shared/validation/auth.schema";
import { useRegister } from "../lib/auth/useAuth";
import { ApiError } from "../lib/api/client";
import { INPUT_CLASS } from "../lib/forms/inputClass";
import { AuthHero } from "../components/AuthHero";
import { BrandWordmark } from "../components/BrandWordmark";
import { Button } from "../components/ui/Button";
import { Field } from "../components/ui/Field";
import { Reveal } from "../components/ui/Reveal";

export function Register() {
  const [, setLocation] = useLocation();
  const registerMutation = useRegister();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterInput) => {
    try {
      await registerMutation.mutateAsync(data);
      setLocation("/dashboard");
    } catch {
      /* surface via registerMutation.error */
    }
  };

  const serverError = registerMutation.error;

  return (
    <main className="min-h-screen lg:grid lg:grid-cols-2">
      <AuthHero />

      <div className="flex min-h-screen flex-col bg-white">
        <header className="px-6 py-5 lg:hidden">
          <Link href="/" className="inline-flex items-center gap-2">
            <BrandWordmark className="h-6" />
          </Link>
        </header>

        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <Reveal className="w-full max-w-sm">
            <h1 className="font-display text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
              Create your account
            </h1>
            <p className="mt-1.5 text-sm text-neutral-600">
              Join Leena's List to start managing your child's care information.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
              <Field label="Full name" htmlFor="fullName" error={errors.fullName?.message}>
                <input
                  id="fullName"
                  type="text"
                  autoComplete="name"
                  {...register("fullName")}
                  className={INPUT_CLASS}
                />
              </Field>

              <Field label="Email" htmlFor="email" error={errors.email?.message}>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register("email")}
                  className={INPUT_CLASS}
                />
              </Field>

              <Field label="Password" htmlFor="password" error={errors.password?.message}>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  {...register("password")}
                  className={INPUT_CLASS}
                />
              </Field>

              {serverError instanceof ApiError && (
                <p className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                  {serverError.code === "CONFLICT"
                    ? "An account with this email already exists."
                    : serverError.message}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full"
                isLoading={isSubmitting || registerMutation.isPending}
              >
                {registerMutation.isPending ? "Creating account…" : "Create account"}
              </Button>
            </form>

            <p className="mt-6 text-sm text-neutral-600">
              Already have an account?{" "}
              <Link href="/login" className="font-medium text-sky-700 hover:text-sky-800">
                Sign in
              </Link>
            </p>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
